import React, { useEffect, useRef, useState } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  FaTimes,
  FaBoxOpen,
  FaCloudUploadAlt,
  FaFile,
  FaFileImage,
  FaFilePdf,
  FaTrashAlt,
  FaSave,
  FaSpinner,
} from 'react-icons/fa';
import {
  CARRIERS,
  SHIPMENT_ALLOWED_FILE_TYPES,
  SHIPMENT_MAX_FILE_SIZE,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_OPTIONS,
  formatFileSize,
  parseShipmentCost,
  todayInputValue,
} from '../../utils/shipments';
import styles from './ShipmentTracking.module.css';

const emptyForm = {
  subject: '',
  content: '',
  status: SHIPMENT_STATUS.NOT_SENT,
  carrier: '',
  trackingNumber: '',
  shippedAt: '',
  deliveredAt: '',
  cost: '',
  recipientName: '',
  recipientOrg: '',
  recipientPhone: '',
  destination: '',
  senderName: '',
  note: '',
  attachments: [],
};

const fileIconFor = (type = '') => {
  if (type.startsWith('image/')) return FaFileImage;
  if (type === 'application/pdf') return FaFilePdf;
  return FaFile;
};

/**
 * Kargo kaydı ekleme / düzenleme formu.
 * Kaydetmeyi çağıran tarafa bırakır (onSave); yalnızca dosyalar doğrudan
 * Storage'a yüklenir, böylece kullanıcı yüklemeyi anında görür.
 */
const ShipmentModal = ({ show, onHide, visitId, shipment, onSave, defaultSender }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const isEditing = Boolean(shipment);

  useEffect(() => {
    if (!show) return;
    if (shipment) {
      setFormData({
        ...emptyForm,
        ...shipment,
        // Bedel sayı olarak saklanır; formda Türkçe ondalık yazımıyla gösterilir
        cost: shipment.cost == null ? '' : String(shipment.cost).replace('.', ','),
      });
    } else {
      setFormData({ ...emptyForm, senderName: defaultSender || '' });
    }
    setErrors({});
  }, [show, shipment, defaultSender]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  // Durum değişince tarihler otomatik dolar; kullanıcı yine de değiştirebilir.
  const handleStatusChange = (status) => {
    setFormData((prev) => {
      const next = { ...prev, status };
      if (status !== SHIPMENT_STATUS.NOT_SENT && !prev.shippedAt) {
        next.shippedAt = todayInputValue();
      }
      if (status === SHIPMENT_STATUS.DELIVERED && !prev.deliveredAt) {
        next.deliveredAt = todayInputValue();
      }
      if (status !== SHIPMENT_STATUS.DELIVERED) {
        next.deliveredAt = status === SHIPMENT_STATUS.RETURNED ? prev.deliveredAt : '';
      }
      return next;
    });
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const accepted = [];
    files.forEach((file) => {
      if (!SHIPMENT_ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name}: desteklenmeyen dosya türü`);
        return;
      }
      if (file.size > SHIPMENT_MAX_FILE_SIZE) {
        toast.error(`${file.name}: dosya 10 MB sınırını aşıyor`);
        return;
      }
      accepted.push(file);
    });
    if (accepted.length === 0) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        accepted.map(async (file) => {
          const storagePath = `shipments/${visitId}/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, storagePath);
          const snapshot = await uploadBytes(storageRef, file);
          const url = await getDownloadURL(snapshot.ref);
          return {
            name: file.name,
            url,
            type: file.type,
            size: file.size,
            storagePath,
            uploadedAt: new Date().toISOString(),
          };
        })
      );
      setFormData((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploaded],
      }));
      toast.success(
        uploaded.length > 1 ? `${uploaded.length} dosya yüklendi` : 'Dosya yüklendi'
      );
    } catch (error) {
      console.error('Error uploading shipment file:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = (formData.attachments || [])[index];
    if (!attachment) return;
    try {
      if (attachment.storagePath) {
        await deleteObject(ref(storage, attachment.storagePath));
      }
    } catch (error) {
      // Dosya Storage'dan silinemese bile kayıttan düşürülür; aksi halde
      // kullanıcı yanlış yüklediği dosyadan kurtulamaz.
      console.error('Error deleting shipment file:', error);
    }
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.subject.trim()) nextErrors.subject = 'Konu başlığı zorunludur';
    if (!formData.recipientName.trim()) nextErrors.recipientName = 'Alıcı zorunludur';
    if (formData.status !== SHIPMENT_STATUS.NOT_SENT && !formData.carrier) {
      nextErrors.carrier = 'Kargoya verildiyse firma seçin';
    }
    if (Number.isNaN(parseShipmentCost(formData.cost))) {
      nextErrors.cost = 'Geçerli bir tutar girin (örn. 125,50)';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (uploading) {
      toast.error('Dosya yüklemesi bitene kadar bekleyin');
      return;
    }
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        subject: formData.subject.trim(),
        trackingNumber: formData.trackingNumber.trim(),
        cost: parseShipmentCost(formData.cost),
        recipientName: formData.recipientName.trim(),
      });
      onHide();
    } catch (error) {
      // Hata mesajı çağıran tarafta gösteriliyor
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const attachments = formData.attachments || [];

  return (
    <div className={styles.modalOverlay} onClick={() => !saving && onHide()}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderTitle}>
            <FaBoxOpen className={styles.modalHeaderIcon} />
            <h2>{isEditing ? 'Kargo Kaydını Düzenle' : 'Yeni Kargo Kaydı'}</h2>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onHide}
            disabled={saving}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* 1. Gönderi içeriği */}
          <section className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Gönderi Bilgileri</h3>
            <div className={styles.field}>
              <label htmlFor="shipmentSubject">
                Konu Başlığı <span className={styles.required}>*</span>
              </label>
              <input
                id="shipmentSubject"
                type="text"
                className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
                value={formData.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder="Örn. Lab kiti gönderimi"
                maxLength={120}
              />
              {errors.subject && <span className={styles.errorText}>{errors.subject}</span>}
            </div>
            <div className={styles.field}>
              <label htmlFor="shipmentContent">İçerik</label>
              <textarea
                id="shipmentContent"
                className={styles.textarea}
                rows={3}
                value={formData.content}
                onChange={(e) => setField('content', e.target.value)}
                placeholder="Kutuda ne var? Malzeme adı, adet, lot numarası..."
              />
            </div>
          </section>

          {/* 2. Kargo durumu */}
          <section className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Kargo Durumu</h3>
            <div className={styles.statusPicker}>
              {SHIPMENT_STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.statusOption} ${styles[`tone_${option.tone}`]} ${
                    formData.status === option.value ? styles.statusOptionActive : ''
                  }`}
                  onClick={() => handleStatusChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="shipmentCarrier">Kargo Firması</label>
                <select
                  id="shipmentCarrier"
                  className={`${styles.input} ${errors.carrier ? styles.inputError : ''}`}
                  value={formData.carrier}
                  onChange={(e) => setField('carrier', e.target.value)}
                >
                  <option value="">Seçiniz...</option>
                  {CARRIERS.map((carrier) => (
                    <option key={carrier.value} value={carrier.value}>
                      {carrier.label}
                    </option>
                  ))}
                </select>
                {errors.carrier && <span className={styles.errorText}>{errors.carrier}</span>}
              </div>
              <div className={styles.field}>
                <label htmlFor="shipmentTracking">Kargo Takip No</label>
                <input
                  id="shipmentTracking"
                  type="text"
                  className={styles.input}
                  value={formData.trackingNumber}
                  onChange={(e) => setField('trackingNumber', e.target.value)}
                  placeholder="Takip numarası"
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="shipmentShippedAt">Kargoya Veriliş Tarihi</label>
                <input
                  id="shipmentShippedAt"
                  type="date"
                  className={styles.input}
                  value={formData.shippedAt}
                  onChange={(e) => setField('shippedAt', e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="shipmentDeliveredAt">Teslim Tarihi</label>
                <input
                  id="shipmentDeliveredAt"
                  type="date"
                  className={styles.input}
                  value={formData.deliveredAt}
                  min={formData.shippedAt || undefined}
                  onChange={(e) => setField('deliveredAt', e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="shipmentCost">Kargo Bedeli</label>
                <div className={styles.costInput}>
                  <span className={styles.costSymbol}>₺</span>
                  <input
                    id="shipmentCost"
                    type="text"
                    inputMode="decimal"
                    className={`${styles.input} ${errors.cost ? styles.inputError : ''}`}
                    value={formData.cost}
                    onChange={(e) => setField('cost', e.target.value)}
                    placeholder="0,00"
                  />
                </div>
                {errors.cost && <span className={styles.errorText}>{errors.cost}</span>}
              </div>
            </div>
          </section>

          {/* 3. Kime / nereye */}
          <section className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Alıcı ve Adres</h3>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="shipmentRecipient">
                  Kime Gönderildi <span className={styles.required}>*</span>
                </label>
                <input
                  id="shipmentRecipient"
                  type="text"
                  className={`${styles.input} ${errors.recipientName ? styles.inputError : ''}`}
                  value={formData.recipientName}
                  onChange={(e) => setField('recipientName', e.target.value)}
                  placeholder="Ad soyad"
                />
                {errors.recipientName && (
                  <span className={styles.errorText}>{errors.recipientName}</span>
                )}
              </div>
              <div className={styles.field}>
                <label htmlFor="shipmentRecipientOrg">Kurum / Merkez</label>
                <input
                  id="shipmentRecipientOrg"
                  type="text"
                  className={styles.input}
                  value={formData.recipientOrg}
                  onChange={(e) => setField('recipientOrg', e.target.value)}
                  placeholder="Hastane, merkez veya firma"
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="shipmentPhone">Alıcı Telefonu</label>
                <input
                  id="shipmentPhone"
                  type="tel"
                  className={styles.input}
                  value={formData.recipientPhone}
                  onChange={(e) => setField('recipientPhone', e.target.value)}
                  placeholder="05xx xxx xx xx"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="shipmentSender">Gönderen</label>
                <input
                  id="shipmentSender"
                  type="text"
                  className={styles.input}
                  value={formData.senderName}
                  onChange={(e) => setField('senderName', e.target.value)}
                  placeholder="Gönderen kişi"
                />
              </div>
            </div>
            <div className={styles.field}>
              <label htmlFor="shipmentDestination">Nereye Gönderildi (Adres)</label>
              <textarea
                id="shipmentDestination"
                className={styles.textarea}
                rows={2}
                value={formData.destination}
                onChange={(e) => setField('destination', e.target.value)}
                placeholder="Teslimat adresi"
              />
            </div>
          </section>

          {/* 4. Ekler */}
          <section className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Dosyalar</h3>
            <div
              className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                uploadFiles(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <FaSpinner className={`${styles.dropZoneIcon} ${styles.spin}`} />
              ) : (
                <FaCloudUploadAlt className={styles.dropZoneIcon} />
              )}
              <span className={styles.dropZoneText}>
                {uploading
                  ? 'Yükleniyor...'
                  : 'Kargo fişi, irsaliye veya teslim tutanağını sürükleyin ya da seçmek için tıklayın'}
              </span>
              <span className={styles.dropZoneHint}>
                PDF, görsel, Word veya Excel · en fazla 10 MB
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={SHIPMENT_ALLOWED_FILE_TYPES.join(',')}
                onChange={(e) => {
                  uploadFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            {attachments.length > 0 && (
              <ul className={styles.fileList}>
                {attachments.map((file, index) => {
                  const Icon = fileIconFor(file.type);
                  return (
                    <li key={file.storagePath || `${file.name}-${index}`} className={styles.fileItem}>
                      <Icon className={styles.fileIcon} />
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.fileName}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {file.name}
                      </a>
                      <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                      <button
                        type="button"
                        className={styles.fileRemove}
                        onClick={() => removeAttachment(index)}
                        title="Dosyayı sil"
                      >
                        <FaTrashAlt />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={styles.formSection}>
            <div className={styles.field}>
              <label htmlFor="shipmentNote">Not</label>
              <textarea
                id="shipmentNote"
                className={styles.textarea}
                rows={2}
                value={formData.note}
                onChange={(e) => setField('note', e.target.value)}
                placeholder="Eklemek istediğiniz not"
              />
            </div>
          </section>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onHide}
            disabled={saving}
          >
            Vazgeç
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={saving || uploading}
          >
            {saving ? <FaSpinner className={styles.spin} /> : <FaSave />}
            {saving ? 'Kaydediliyor...' : isEditing ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShipmentModal;
